import React, { useState, useEffect } from "react";
import { Modal, Button, Icon, TextArea } from "semantic-ui-react";
import classNames from "classnames";
import { map, forEach } from "lodash";
import "./ProductCustomizeModal.scss";

export function ProductCustomizeModal(props) {
  const { product, show, onClose, onConfirm } = props;
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [extraQuantities, setExtraQuantities] = useState({});

  useEffect(() => {
    if (show) {
      setQuantity(1);
      setNote("");
      setSelectedChoices([]);
      setExtraQuantities({});
    }
  }, [show, product]);

  if (!product) return null;

  const option = product.option;
  const choiceCount = option?.choice_count || 1;
  const optionMissing =
    Boolean(option?.required) && selectedChoices.length !== choiceCount;
  const extraGroups = product.extra_groups_data || [];
  const optionChoices = map(option?.choices, normalizeChoice);
  const optionChoice = selectedChoices.join(" + ");
  const optionChoicePrice = selectedChoices.reduce((acc, label) => {
    const choice = optionChoices.find((c) => c.label === label);
    return acc + (choice?.price ? Number(choice.price) : 0);
  }, 0);

  const toggleChoice = (label) => {
    if (choiceCount === 1) {
      setSelectedChoices([label]);
      return;
    }
    setSelectedChoices((prev) => {
      if (prev.includes(label)) return prev.filter((item) => item !== label);
      if (prev.length >= choiceCount) return prev;
      return [...prev, label];
    });
  };

  const increment = () => setQuantity((prev) => prev + 1);
  const decrement = () => setQuantity((prev) => Math.max(1, prev - 1));

  const setExtraQuantity = (itemId, value) => {
    setExtraQuantities((prev) => ({
      ...prev,
      [itemId]: Math.max(0, value),
    }));
  };

  const selectedExtras = [];
  forEach(extraGroups, (group) => {
    forEach(group.items, (item) => {
      const itemQuantity = extraQuantities[item.id] || 0;
      if (itemQuantity > 0) {
        selectedExtras.push({
          id: item.id,
          label: item.label,
          price: item.price,
          estimated_cost: item.estimated_cost,
          quantity: itemQuantity,
        });
      }
    });
  });

  // Los adicionales son un costo compartido (ej. una porción de arroz para
  // 2 platos), no se multiplican por la cantidad del plato principal —
  // cada adicional ya tiene su propio contador de cantidad independiente.
  const extrasTotal = selectedExtras.reduce(
    (acc, extra) => acc + Number(extra.price) * extra.quantity,
    0
  );
  const unitPrice = Number(product.price) + optionChoicePrice;

  const handleConfirm = () => {
    if (optionMissing) return;
    onConfirm({
      id: product.id,
      quantity,
      note,
      optionChoice,
      extras: selectedExtras,
      unitPrice,
    });
  };

  const total = (unitPrice * quantity + extrasTotal).toFixed(2);

  return (
    <Modal
      className="product-customize-modal"
      open={show}
      onClose={onClose}
      size="tiny"
    >
      <Button
        icon
        basic
        labelPosition="left"
        className="product-customize-modal__back"
        onClick={onClose}
      >
        <Icon name="arrow left" />
        Regresar al Menu
      </Button>

      <Modal.Content>
        <div className="product-customize-modal__header">
          <div>
            <p className="product-customize-modal__subtitle">
              Personaliza tu
            </p>
            <h3>{product.title}</h3>
          </div>
          {product.image && (
            <img
              className="product-customize-modal__image"
              src={product.image}
              alt={product.title}
            />
          )}
        </div>

        {option && (
          <div
            className={classNames(
              "product-customize-modal__section",
              "product-customize-modal__option",
              { "product-customize-modal__option--missing": optionMissing }
            )}
          >
            <div className="product-customize-modal__option-head">
              <label>
                {option.label}
                {option.required && (
                  <span className="product-customize-modal__required">
                    Requerido
                  </span>
                )}
              </label>
              {choiceCount > 1 && (
                <span className="product-customize-modal__choice-counter">
                  {selectedChoices.length} / {choiceCount}
                </span>
              )}
            </div>

            <div className="product-customize-modal__choices">
              {map(optionChoices, (choice) => {
                const isSelected = selectedChoices.includes(choice.label);
                const isMaxed =
                  choiceCount > 1 &&
                  !isSelected &&
                  selectedChoices.length >= choiceCount;

                return (
                  <button
                    type="button"
                    key={choice.label}
                    disabled={isMaxed}
                    className={classNames("product-customize-modal__choice", {
                      "product-customize-modal__choice--selected": isSelected,
                    })}
                    onClick={() => toggleChoice(choice.label)}
                  >
                    {choice.label}
                    {choice.price && ` (S/ ${choice.price})`}
                  </button>
                );
              })}

              {choiceCount > 1 && selectedChoices.length === choiceCount && (
                <div className="product-customize-modal__cheers">
                  ¡Listo! Combo completo 🎉
                </div>
              )}
            </div>
          </div>
        )}

        {map(extraGroups, (group) => (
          <div className="product-customize-modal__section" key={group.id}>
            <label>{group.label}</label>

            <div className="product-customize-modal__extra-items">
              {map(group.items, (item) => (
                <div className="product-customize-modal__extra-item" key={item.id}>
                  <span>
                    {item.label} <b>S/ {item.price}</b>
                  </span>
                  <div className="product-customize-modal__quantity">
                    <button
                      type="button"
                      onClick={() =>
                        setExtraQuantity(
                          item.id,
                          (extraQuantities[item.id] || 0) - 1
                        )
                      }
                      disabled={!extraQuantities[item.id]}
                    >
                      −
                    </button>
                    <span>{extraQuantities[item.id] || 0}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setExtraQuantity(
                          item.id,
                          (extraQuantities[item.id] || 0) + 1
                        )
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="product-customize-modal__section">
          <label>Nota para cocina:</label>
          <TextArea value={note} onChange={(_, data) => setNote(data.value)} />
        </div>

        <div className="product-customize-modal__section">
          <label>Cantidad</label>
          <div className="product-customize-modal__quantity">
            <button
              type="button"
              onClick={decrement}
              disabled={quantity <= 1}
            >
              −
            </button>
            <span>{quantity}</span>
            <button type="button" onClick={increment}>
              +
            </button>
          </div>
        </div>
      </Modal.Content>

      <Modal.Actions>
        {optionMissing && (
          <p className="product-customize-modal__warning">
            Falta elegir una opción arriba para poder agregar al carrito.
          </p>
        )}
        <Button
          primary
          fluid
          disabled={optionMissing}
          onClick={handleConfirm}
        >
          Agregar al Carrito (S/ {total})
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

function normalizeChoice(choice) {
  return typeof choice === "string" ? { label: choice, price: null } : choice;
}
